import { useEffect, useMemo, useState } from "react";
import { Check, Filter, FilterX, Search, Sigma, Scale, MousePointerClick } from "lucide-react";
import {
  applyFilter,
  setFilter,
  removeFilter,
  getColumnFilter,
  clearAll,
  clearPicks,
  columnStats,
} from "../../lib/quickFilter";

// The one Filter flyout of the ERP right-click menu, in three modes:
//
//   Clicked  — filter by an exact value. Right-clicked a ROW: every column of
//              that row. Right-clicked a column HEADER: that column's distinct
//              values, plus contains / excludes / blank checks. Either way,
//              "is one of" when you have a multi-cell selection.
//   Math     — arithmetic. Sum / difference / product / average / min / max of
//              the cells you Alt+right-clicked, and live totals for any numeric
//              column over the rows currently visible. Any result can be turned
//              straight into a ≥ / ≤ / = filter.
//   Compare  — greater/smaller/between against a typed number or against the
//              cells you selected ("between these two" is one click).
//
// Alt + right-click a body cell adds it to the selection (see togglePick in
// lib/quickFilter.js); the strip at the top of this panel shows what's picked.

const LIST_CAP = 300; // don't render thousands of checkboxes

const TABS = [
  { key: "clicked", label: "Clicked", icon: MousePointerClick },
  { key: "math", label: "Math", icon: Sigma },
  { key: "compare", label: "Compare", icon: Scale },
];

const MATH_OPS = [
  { key: "sum", label: "Sum", sign: "+" },
  { key: "diff", label: "Diff", sign: "−" },
  { key: "product", label: "×", sign: "×" },
  { key: "avg", label: "Avg", sign: "avg" },
  { key: "min", label: "Min", sign: "min" },
  { key: "max", label: "Max", sign: "max" },
];

const CMP_OPS = [
  { op: "gte", label: "≥" },
  { op: "lte", label: "≤" },
  { op: "gt", label: ">" },
  { op: "lt", label: "<" },
  { op: "eq", label: "=" },
  { op: "ne", label: "≠" },
];

const fmt = (n) =>
  n === null || n === undefined || !Number.isFinite(n)
    ? "—"
    : n.toLocaleString("en-IN", { maximumFractionDigits: 2 });

function compute(op, ns) {
  if (!ns.length) return null;
  switch (op) {
    case "sum": return ns.reduce((a, b) => a + b, 0);
    case "diff": return ns.reduce((a, b) => a - b);
    case "product": return ns.reduce((a, b) => a * b, 1);
    case "avg": return ns.reduce((a, b) => a + b, 0) / ns.length;
    case "min": return Math.min(...ns);
    case "max": return Math.max(...ns);
    default: return null;
  }
}

const pill = (on) =>
  `px-2 h-6 rounded-md text-[11px] font-medium border transition-colors whitespace-nowrap ${
    on
      ? "bg-primary text-primary-foreground border-primary"
      : "border-border text-muted-foreground hover:bg-muted hover:text-foreground"
  }`;

const ghost =
  "h-7 rounded-md border border-border text-[11px] font-medium hover:bg-muted transition-colors px-2";

const RowFilterFlyout = ({ filter, picks, active, style, onMouseEnter, onDone }) => {
  const [tab, setTab] = useState("clicked");
  const [mathOp, setMathOp] = useState("sum");
  const [statCol, setStatCol] = useState(null);
  const [cmpOp, setCmpOp] = useState("gte");
  const [cmpVal, setCmpVal] = useState("");
  // Header mode only: the distinct-value checklist and its search box.
  const [valSearch, setValSearch] = useState("");
  const [checked, setChecked] = useState(null); // Set of kept values, or null = all
  const [text, setText] = useState("");

  // Multi-column targeting: Text and Compare can be aimed at several columns
  // at once (a row passes if ANY of them matches).
  const [selCols, setSelCols] = useState(null); // Set<colIndex>, seeded on mount
  const [colSearch, setColSearch] = useState("");

  const { table, columns, column } = filter;
  const isHeader = filter.source === "header";

  // Numeric columns in table order — what Math and Compare can operate on.
  const numCols = useMemo(
    () => columns.filter((c) => c.kind === "number").sort((a, b) => a.colIndex - b.colIndex),
    [columns]
  );
  const clickedCol = isHeader
    ? columns.find((c) => c.colIndex === column.colIndex)
    : columns.find((c) => c.clicked) || columns[0];

  // Sensible defaults: whatever you right-clicked, else the first numeric column.
  useEffect(() => {
    const preferred =
      numCols.find((c) => c.colIndex === picks.sameColumn) ||
      (clickedCol?.kind === "number" ? clickedCol : null) ||
      numCols[0] ||
      null;
    setStatCol((s) => s ?? preferred?.colIndex ?? null);
    setCmpVal((v) => (v === "" && preferred?.number != null ? String(preferred.number) : v));
    setSelCols((s) => s ?? new Set(clickedCol ? [clickedCol.colIndex] : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(
    () => (statCol === null ? null : columnStats(table, statCol)),
    [table, statCol, active]
  );

  const mathResult = compute(mathOp, picks.numbers);
  const pickedMin = picks.numbers.length ? Math.min(...picks.numbers) : null;
  const pickedMax = picks.numbers.length ? Math.max(...picks.numbers) : null;
  const labelOf = (colIndex) =>
    columns.find((c) => c.colIndex === colIndex)?.label || `Col ${colIndex + 1}`;

  const applyNum = (colIndex, op, a, b) => {
    if (colIndex === null || a === null || !Number.isFinite(a)) return;
    setFilter({ table, colIndex, label: labelOf(colIndex), type: "num", op, a, b });
    onDone();
  };

  // ── Column multi-select ──────────────────────────────────────────────────
  // Drives Text and Compare: with 2+ columns chosen the filter matches a row
  // if ANY of them matches, so "contains PARIKH across Customer + Invoice #"
  // is one action.
  const sel = selCols || new Set();
  const selList = columns.filter((c) => sel.has(c.colIndex)).sort((a, b) => a.colIndex - b.colIndex);
  const selNum = selList.filter((c) => c.kind === "number");
  const selLabel = selList.map((c) => c.label).join(" / ") || "—";
  const singleCol = selList.length === 1 ? selList[0] : null;

  const shownCols = useMemo(() => {
    const q = colSearch.trim().toLowerCase();
    const list = q ? columns.filter((c) => c.label.toLowerCase().includes(q)) : columns;
    return list.slice().sort((a, b) => a.colIndex - b.colIndex);
  }, [columns, colSearch]);

  const toggleCol = (colIndex) => {
    const next = new Set(sel);
    if (next.has(colIndex)) next.delete(colIndex);
    else next.add(colIndex);
    setSelCols(next);
  };

  // Target descriptor for a filter aimed at the current selection.
  const selTarget = () =>
    selList.length === 1
      ? { table, colIndex: selList[0].colIndex, label: selList[0].label }
      : { table, colIndexes: selList.map((c) => c.colIndex), any: true, label: selLabel };

  // Plain JSX, NOT a nested component — a component defined inside render gets
  // a fresh type every render, which would remount the search input and eat
  // every keystroke's focus.
  const columnPicker = (
    <div className="p-1.5 border-b border-border">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Columns
        </span>
        <div className="flex-1" />
        <span className="text-[10px] text-muted-foreground truncate max-w-[150px]" title={selLabel}>
          {selList.length ? `${selList.length} selected` : "none"}
        </span>
      </div>
      {columns.length > 6 && (
        <div className="relative mb-1.5">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            value={colSearch}
            onChange={(e) => setColSearch(e.target.value)}
            placeholder="Search columns…"
            className="w-full h-7 rounded-md border border-border bg-background pl-7 pr-2 text-xs outline-none focus:border-primary"
          />
        </div>
      )}
      <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto overscroll-contain scrollbar-hide">
        {shownCols.map((c) => (
          <button
            key={c.colIndex}
            type="button"
            onClick={() => toggleCol(c.colIndex)}
            className={pill(sel.has(c.colIndex))}
            title={`${c.label} · ${c.kind}`}
          >
            {c.label}
          </button>
        ))}
        {!shownCols.length && (
          <span className="text-[11px] text-muted-foreground px-1">No column matches</span>
        )}
      </div>
      {selList.length > 1 && (
        <div className="mt-1.5 text-[10px] text-muted-foreground leading-snug">
          Matches a row if <span className="text-foreground font-medium">any</span> of these columns matches.
        </div>
      )}
    </div>
  );

  // ── Header mode helpers ──────────────────────────────────────────────────
  const shownValues = useMemo(() => {
    if (!isHeader) return [];
    const q = valSearch.trim().toLowerCase();
    const list = q ? column.values.filter((v) => v.value.toLowerCase().includes(q)) : column.values;
    return list.slice(0, LIST_CAP);
  }, [isHeader, column, valSearch]);

  const applyValues = (next) => {
    setChecked(next);
    if (!next || next.size === 0 || next.size === column.values.length) removeFilter(column.colIndex);
    else setFilter({ table, colIndex: column.colIndex, label: column.label, type: "in", kind: column.kind, values: Array.from(next) });
  };

  const toggleValue = (value) => {
    const next = new Set(checked ?? column.values.map((v) => v.value));
    if (next.has(value)) next.delete(value);
    else next.add(value);
    applyValues(next);
  };

  // Text tests hit whatever columns are selected in the picker.
  const applyText = (type) => {
    const t = text.trim();
    if (!t || !selList.length) return;
    setFilter({ ...selTarget(), type, text: t });
    onDone();
  };

  const applyBlank = (type) => {
    if (!selList.length) return;
    setFilter({ ...selTarget(), type });
    onDone();
  };

  // Compare hits the numeric columns of the selection.
  const applyCompare = (op, a, b) => {
    if (!selNum.length || a === null || a === undefined || !Number.isFinite(a)) return;
    const target =
      selNum.length === 1
        ? { table, colIndex: selNum[0].colIndex, label: selNum[0].label }
        : {
            table,
            colIndexes: selNum.map((c) => c.colIndex),
            any: true,
            label: selNum.map((c) => c.label).join(" / "),
          };
    setFilter({ ...target, type: "num", op, a, b });
    onDone();
  };

  // Plain function, not a component — same remount reasoning as columnPicker.
  const colPills = (value, onChange) => (
    <div className="flex flex-wrap gap-1 mb-2">
      {numCols.map((c) => (
        <button key={c.colIndex} type="button" className={pill(value === c.colIndex)} onClick={() => onChange(c.colIndex)}>
          {c.label}
        </button>
      ))}
    </div>
  );

  return (
    <div
      style={style}
      onMouseEnter={onMouseEnter}
      className="z-[301] flex flex-col rounded-lg border border-border bg-popover text-popover-foreground shadow-xl animate-in fade-in zoom-in-95 duration-100"
    >
      {/* Header mode states which column everything here is scoped to. */}
      {isHeader && (
        <div className="flex items-center gap-2 px-2.5 py-2 border-b border-border">
          <span className="w-5 h-5 rounded bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <Filter className="w-3 h-3" />
          </span>
          <span className="flex-1 min-w-0 truncate text-xs font-semibold">{column.label}</span>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground flex-shrink-0">
            {column.kind} · {column.rowCount}
          </span>
        </div>
      )}

      {/* Selection strip — what Alt + right-click has picked up so far */}
      {picks.items.length > 0 && (
        <div className="px-2 pt-2 pb-1.5 border-b border-border">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
              Selected {picks.items.length}
            </span>
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => clearPicks()}
              className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          </div>
          <div className="flex flex-wrap gap-1">
            {picks.items.map((p, i) => (
              <span
                key={`${p.colIndex}-${p.text}-${i}`}
                className="inline-flex items-center gap-1 rounded-md bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] max-w-full"
              >
                <span className="opacity-70">{p.label}</span>
                <span className="font-semibold truncate max-w-[110px]">{p.text}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Mode tabs */}
      <div className="flex items-center gap-0.5 p-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`flex-1 inline-flex items-center justify-center gap-1 h-7 rounded-md text-[11px] font-medium transition-colors ${
              tab === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
            }`}
          >
            <t.icon className="w-3 h-3" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Which columns the controls below aim at. Not shown on Clicked-in-row
          mode (that tab filters one column per entry) or on Math (its totals
          are per-column by nature and it has its own picker). */}
      {(tab === "compare" || (tab === "clicked" && isHeader)) && columnPicker}

      <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-hide p-1">
        {/* ── CLICKED ─────────────────────────────────────────────────── */}
        {tab === "clicked" && (
          <>
            {picks.sameColumn !== null && picks.items.length > 1 && (
              <button
                type="button"
                onClick={() => {
                  setFilter({
                    table,
                    colIndex: picks.sameColumn,
                    label: labelOf(picks.sameColumn),
                    type: "in",
                    kind: "text",
                    values: picks.items.map((p) => p.text),
                  });
                  onDone();
                }}
                className="w-full flex items-center gap-2 px-2.5 h-8 mb-1 rounded-md text-xs bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-left"
              >
                <Filter className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="flex-1 truncate font-medium">
                  Show only these {picks.items.length} {labelOf(picks.sameColumn)} values
                </span>
              </button>
            )}
            {/* Row mode: every column of the row you right-clicked. */}
            {!isHeader && (
              <>
                <div className="px-1.5 pt-0.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Filter this page by
                </div>
                {columns.map((col) => (
                  <button
                    key={col.colIndex}
                    type="button"
                    role="menuitem"
                    onClick={() => { applyFilter({ table, colIndex: col.colIndex, label: col.label, value: col.value, match: col.match }); onDone(); }}
                    className="w-full flex items-center gap-2 px-2.5 h-8 rounded-md text-sm hover:bg-muted transition-colors text-left"
                  >
                    <Filter className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="flex-1 min-w-0 truncate">
                      <span className="text-muted-foreground">{col.label}: </span>
                      <span className="font-medium text-foreground">{col.value}</span>
                    </span>
                    {col.clicked && (
                      <span className="text-[10px] uppercase tracking-wide text-primary flex-shrink-0">clicked</span>
                    )}
                  </button>
                ))}
              </>
            )}

            {/* Header mode: text tests on the selected columns, plus the
                right-clicked column's distinct values. */}
            {isHeader && (
              <div className="p-1.5 space-y-3">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Search {selList.length > 1 ? `${selList.length} columns` : "this column"}
                  </div>
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && applyText("contains")}
                    placeholder={`Search in ${selLabel}…`}
                    className="w-full h-7 rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-primary mb-1.5"
                  />
                  <div className="grid grid-cols-2 gap-1">
                    <button type="button" className={ghost} onClick={() => applyText("contains")}>Contains</button>
                    <button type="button" className={ghost} onClick={() => applyText("notcontains")}>Excludes</button>
                    <button type="button" className={ghost} onClick={() => applyBlank("notblank")}>Has value</button>
                    <button type="button" className={ghost} onClick={() => applyBlank("blank")}>
                      Blank{singleCol && singleCol.colIndex === column.colIndex && column.blanks ? ` (${column.blanks})` : ""}
                    </button>
                  </div>
                </div>

                <div className="border-t border-border pt-2.5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground truncate">
                      {column.label} values · {column.values.length}
                    </span>
                    <div className="flex-1" />
                    {checked && (
                      <button
                        type="button"
                        onClick={() => applyValues(null)}
                        className="text-[10px] font-medium uppercase tracking-wider text-primary hover:underline"
                      >
                        Select all
                      </button>
                    )}
                  </div>
                  <div className="relative mb-1.5">
                    <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input
                      value={valSearch}
                      onChange={(e) => setValSearch(e.target.value)}
                      placeholder="Search values…"
                      className="w-full h-7 rounded-md border border-border bg-background pl-7 pr-2 text-xs outline-none focus:border-primary"
                    />
                  </div>
                  <div className="max-h-44 overflow-y-auto overscroll-contain scrollbar-hide -mx-0.5">
                    {shownValues.map((v) => {
                      const on = checked ? checked.has(v.value) : true;
                      return (
                        <div
                          key={v.value}
                          className="group flex items-center gap-2 pl-1.5 pr-1 h-7 rounded-md hover:bg-muted transition-colors"
                        >
                          <button
                            type="button"
                            onClick={() => toggleValue(v.value)}
                            className="flex-1 min-w-0 flex items-center gap-2 text-left h-full"
                          >
                            <span
                              className={`w-3.5 h-3.5 rounded-[3px] border flex items-center justify-center flex-shrink-0 transition-colors ${
                                on ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"
                              }`}
                            >
                              {on && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                            </span>
                            <span className="flex-1 min-w-0 truncate text-xs">{v.value}</span>
                          </button>
                          <span className="text-[10px] text-muted-foreground tabular-nums flex-shrink-0 group-hover:hidden">
                            {v.count}
                          </span>
                          <button
                            type="button"
                            onClick={() => applyValues(new Set([v.value]))}
                            title={`Show only ${v.value}`}
                            className="hidden group-hover:block text-[10px] font-medium uppercase tracking-wider text-primary flex-shrink-0"
                          >
                            only
                          </button>
                        </div>
                      );
                    })}
                    {!shownValues.length && (
                      <div className="px-1.5 py-2 text-[11px] text-muted-foreground">No matching values</div>
                    )}
                    {column.values.length > shownValues.length && (
                      <div className="px-1.5 py-1.5 text-[10px] text-muted-foreground">
                        +{column.values.length - shownValues.length} more — search to narrow
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── MATH ────────────────────────────────────────────────────── */}
        {tab === "math" && (
          <div className="p-1.5 space-y-3">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                Selected cells
              </div>
              {picks.numbers.length ? (
                <>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {MATH_OPS.map((o) => (
                      <button key={o.key} type="button" className={pill(mathOp === o.key)} onClick={() => setMathOp(o.key)}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                  <div className="rounded-md border border-border bg-muted/40 px-2.5 py-2 mb-2">
                    <div className="text-[10px] text-muted-foreground truncate">
                      {picks.numbers.map(fmt).join(mathOp === "diff" ? " − " : mathOp === "product" ? " × " : ", ")}
                    </div>
                    <div className="text-base font-bold font-mono tabular-nums leading-6">{fmt(mathResult)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    {["gte", "lte", "eq"].map((op) => (
                      <button
                        key={op}
                        type="button"
                        onClick={() => applyNum(statCol, op, mathResult)}
                        className={`${ghost} flex-1`}
                      >
                        {CMP_OPS.find((c) => c.op === op).label} result
                      </button>
                    ))}
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    applies to <span className="text-foreground font-medium">{statCol !== null ? labelOf(statCol) : "—"}</span>
                  </div>
                </>
              ) : (
                <div className="rounded-md border border-dashed border-border px-2.5 py-3 text-[11px] text-muted-foreground leading-relaxed">
                  <span className="font-semibold text-foreground">Alt + right-click</span> number cells to add
                  them here, then sum / subtract / average them.
                </div>
              )}
            </div>

            <div className="border-t border-border pt-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                Column totals · visible rows
              </div>
              {numCols.length ? (
                <>
                  {colPills(statCol, setStatCol)}
                  {stats ? (
                    <>
                      <div className="grid grid-cols-2 gap-1 mb-2">
                        {[
                          ["Sum", stats.sum],
                          ["Average", stats.avg],
                          ["Min", stats.min],
                          ["Max", stats.max],
                        ].map(([k, v]) => (
                          <div key={k} className="rounded-md border border-border px-2 py-1">
                            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{k}</div>
                            <div className="text-[11px] font-mono font-semibold tabular-nums truncate">{fmt(v)}</div>
                          </div>
                        ))}
                      </div>
                      <div className="text-[10px] text-muted-foreground mb-1.5">
                        {stats.count} of {stats.visibleRows} visible rows have a number
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        <button type="button" className={ghost} onClick={() => applyNum(statCol, "gt", stats.avg)}>
                          Above average
                        </button>
                        <button type="button" className={ghost} onClick={() => applyNum(statCol, "lt", stats.avg)}>
                          Below average
                        </button>
                        <button type="button" className={ghost} onClick={() => applyNum(statCol, "eq", stats.max)}>
                          Highest only
                        </button>
                        <button type="button" className={ghost} onClick={() => applyNum(statCol, "eq", stats.min)}>
                          Lowest only
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="text-[11px] text-muted-foreground">No numbers in view.</div>
                  )}
                </>
              ) : (
                <div className="text-[11px] text-muted-foreground">This table has no numeric column.</div>
              )}
            </div>
          </div>
        )}

        {/* ── COMPARE ─────────────────────────────────────────────────── */}
        {tab === "compare" && (
          <div className="p-1.5 space-y-3">
            {numCols.length ? (
              <>
                <div className="text-[10px] text-muted-foreground -mb-1">
                  Comparing{" "}
                  <span className="text-foreground font-medium">
                    {selNum.length ? selNum.map((c) => c.label).join(" / ") : "— pick a number column above"}
                  </span>
                </div>

                {picks.numbers.length >= 2 && (
                  <button
                    type="button"
                    onClick={() => applyCompare("between", pickedMin, pickedMax)}
                    className="w-full h-8 rounded-md bg-primary/10 text-primary text-[11px] font-semibold hover:bg-primary/20 transition-colors px-2 truncate"
                  >
                    Between {fmt(pickedMin)} and {fmt(pickedMax)}
                  </button>
                )}

                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Compare with
                  </div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {CMP_OPS.map((o) => (
                      <button key={o.op} type="button" className={`${pill(cmpOp === o.op)} w-8 px-0`} onClick={() => setCmpOp(o.op)}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={cmpVal}
                      onChange={(e) => setCmpVal(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && applyCompare(cmpOp, parseFloat(cmpVal))}
                      placeholder="value"
                      className="flex-1 h-7 rounded-md border border-border bg-background px-2 text-xs font-mono tabular-nums outline-none focus:border-primary"
                    />
                    <button
                      type="button"
                      onClick={() => applyCompare(cmpOp, parseFloat(cmpVal))}
                      className="h-7 px-3 rounded-md bg-primary text-primary-foreground text-[11px] font-semibold hover:opacity-90 transition-opacity"
                    >
                      Apply
                    </button>
                  </div>
                </div>

                {picks.numbers.length > 0 && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Or use a selected value
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {picks.numbers.map((n, i) => (
                        <button
                          key={`${n}-${i}`}
                          type="button"
                          onClick={() => setCmpVal(String(n))}
                          className={`${ghost} font-mono tabular-nums`}
                        >
                          {fmt(n)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-[11px] text-muted-foreground p-1">
                This table has no numeric column to compare.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-border p-1">
        <div className="px-1.5 py-1 text-[10px] text-muted-foreground leading-snug">
          <span className="font-semibold text-foreground">Alt + right-click</span> cells to select values
        </div>
        {isHeader && (
          <button
            type="button"
            onClick={() => {
              // The filter on this column may be a multi-column one, whose key
              // isn't the bare colIndex — look it up rather than guessing.
              const f = getColumnFilter(table, column.colIndex);
              removeFilter(f ? f.key : column.colIndex);
              setChecked(null);
              onDone();
            }}
            className="w-full flex items-center gap-2 px-2.5 h-8 rounded-md text-sm hover:bg-muted transition-colors text-left"
          >
            <FilterX className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            <span className="flex-1 truncate">Clear {column.label} filter</span>
          </button>
        )}
        {active.active && (
          <button
            type="button"
            onClick={() => { clearAll(); onDone(); }}
            className="w-full flex items-center gap-2 px-2.5 h-8 rounded-md text-sm hover:bg-muted transition-colors text-left"
          >
            <FilterX className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            <span className="flex-1 truncate">
              Clear {active.filters.length > 1 ? "all filters" : "filter"}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default RowFilterFlyout;
